import axiosInstance from "../../../../lib/axiosInstance";

// 200 MB — mirrors MAX_UPLOAD_BYTES on the backend. Rejecting here saves a
// round-trip and a clearer message than the server's 422.
export const MAX_UPLOAD_BYTES = 200 * 1024 * 1024;

const detailMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (!detail) return fallback;
  // FastAPI 422 returns detail as an array of objects.
  return typeof detail === "string" ? detail : JSON.stringify(detail);
};

const requestUploadUrl = async ({ fileName, sizeBytes, deviceId }) => {
  try {
    const response = await axiosInstance.post("/telemetry/logs/upload-url", {
      file_name: fileName,
      size_bytes: sizeBytes,
      device_id: deviceId || null,
    });
    return response.data; // { log: {id, s3_key, status}, upload_url }
  } catch (error) {
    throw new Error(detailMessage(error, "Failed to start upload"));
  }
};

// Raw PUT straight to S3. Deliberately NOT axiosInstance: S3 must not receive
// our Authorization header, the Content-Type must match what was presigned, and
// we want real upload progress (fetch has none). The presigned URL is the auth.
export const putToS3 = (uploadUrl, file, onProgress) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl, true);
    xhr.setRequestHeader("Content-Type", "application/octet-stream");
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("Upload failed — network error"));
    xhr.send(file);
  });

const startParse = async (logId) => {
  try {
    const response = await axiosInstance.post(`/telemetry/logs/${logId}/parse`);
    return response.data;
  } catch (error) {
    // Keep the response on the error so callers can tell 409 (already parsing)
    // from a real failure.
    const wrapped = new Error(detailMessage(error, "Failed to start parsing"));
    wrapped.response = error?.response;
    throw wrapped;
  }
};

/**
 * Re-run the parse on a log that is already uploaded.
 *
 * The same endpoint the upload flow calls: it re-reads the stored raw log and
 * rebuilds everything derived from it, which is how a log parsed by an older
 * build picks up analysis it never had. 409 means a parse is already running —
 * the caller's own polling will pick that up, so it is not an error here.
 */
export const reparseLog = async (logId) => {
  try {
    return await startParse(logId);
  } catch (error) {
    if (error?.response?.status === 409) return { log: { id: logId, status: "parsing" } };
    throw error;
  }
};

/**
 * Full upload flow: presign → PUT → kick parse. Returns the created log's id.
 * onProgress(pct) reports the S3 upload only (0–100).
 */
export const uploadLog = async (file, { deviceId, onProgress } = {}) => {
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(`File is too large (max ${Math.round(MAX_UPLOAD_BYTES / 1024 / 1024)} MB)`);
  }
  const { log, upload_url } = await requestUploadUrl({
    fileName: file.name,
    sizeBytes: file.size,
    deviceId,
  });
  await putToS3(upload_url, file, onProgress);
  await startParse(log.id);
  return log.id;
};

export const listLogs = async ({ pageNumber = 1, pageSize = 20 } = {}) => {
  try {
    const response = await axiosInstance.get("/telemetry/logs", {
      params: { page_number: pageNumber, page_size: pageSize },
    });
    return response.data; // { logs, count }
  } catch (error) {
    throw new Error(detailMessage(error, "Failed to load logs"));
  }
};

export const getLog = async (logId) => {
  try {
    const response = await axiosInstance.get(`/telemetry/logs/${logId}`);
    return response.data.log;
  } catch (error) {
    throw new Error(detailMessage(error, "Failed to load log"));
  }
};

export const getMessages = async (logId, { type, fields, maxPoints } = {}) => {
  try {
    const response = await axiosInstance.get(`/telemetry/logs/${logId}/messages`, {
      params: {
        type,
        fields: fields?.length ? fields.join(",") : undefined,
        max_points: maxPoints || undefined,
      },
    });
    return response.data; // { type, fields, count, total, truncated, points }
  } catch (error) {
    throw new Error(detailMessage(error, "Failed to load series"));
  }
};

/**
 * A short-lived link to the original uploaded log.
 *
 * The raw file is kept forever and never rewritten, so this is the exact bytes
 * the aircraft produced - for re-analysis elsewhere, or for any other tool.
 */
export const getDownloadUrl = async (logId) => {
  try {
    const response = await axiosInstance.get(`/telemetry/logs/${logId}/download`);
    return response.data.download; // { id, file_name, url, expires_in }
  } catch (error) {
    throw new Error(detailMessage(error, "Failed to get a download link"));
  }
};

/**
 * Links for several logs at once.
 *
 * One link per log rather than a server-built archive: raw logs run to hundreds
 * of megabytes and zipping them would stream every byte through the API. Logs
 * the caller does not own are simply absent from the response.
 */
export const getBulkDownloadUrls = async (logIds) => {
  try {
    const response = await axiosInstance.post("/telemetry/logs/download-urls", { ids: logIds });
    return response.data; // { downloads, count, requested }
  } catch (error) {
    throw new Error(detailMessage(error, "Failed to get download links"));
  }
};

export const updateLogTags = async (logId, tags) => {
  try {
    const response = await axiosInstance.patch(`/telemetry/logs/${logId}/tags`, { tags });
    return response.data.log; // updated log with normalised tags
  } catch (error) {
    throw new Error(detailMessage(error, "Failed to update tags"));
  }
};

export const setLogPublic = async (logId, isPublic) => {
  try {
    const response = await axiosInstance.patch(`/telemetry/logs/${logId}/public`, { is_public: isPublic });
    return response.data.log;
  } catch (error) {
    throw new Error(detailMessage(error, "Failed to update visibility"));
  }
};

/**
 * Point a log at a drone in the catalog, or clear it with null.
 *
 * `ref` is source-qualified ("fleet:<id>", "devices:<id>", "propulsion:<id>")
 * because the three registries have separate id spaces — a bare uuid could not
 * say which one it came from.
 *
 * ponytail: PATCH /telemetry/logs/{id} does not exist yet. The column probably
 * does: upload already accepts device_id and GET /telemetry/logs already filters
 * by it. Callers surface the 404 rather than pretending it saved.
 */
export const setLogDrone = async (logId, ref) => {
  try {
    const response = await axiosInstance.patch(`/telemetry/logs/${logId}`, { drone_ref: ref });
    return response.data.log;
  } catch (error) {
    throw new Error(detailMessage(error, "Failed to associate a drone"));
  }
};

export const deleteLog = async (logId) => {
  try {
    await axiosInstance.delete(`/telemetry/logs/${logId}`);
  } catch (error) {
    throw new Error(detailMessage(error, "Failed to delete log"));
  }
};

/* ------------------------------------------------------------ comparisons ---- */

export const listComparisons = async ({ pageNumber = 1, pageSize = 50 } = {}) => {
  try {
    const response = await axiosInstance.get("/telemetry/comparisons", {
      params: { page_number: pageNumber, page_size: pageSize },
    });
    return response.data; // { comparisons, count }
  } catch (error) {
    throw new Error(detailMessage(error, "Failed to load comparisons"));
  }
};

export const getComparison = async (id) => {
  try {
    const response = await axiosInstance.get(`/telemetry/comparisons/${id}`);
    return response.data; // { comparison, base, compares, highlights }
  } catch (error) {
    throw new Error(detailMessage(error, "Failed to load comparison"));
  }
};

export const createComparison = async ({ name, baseLogId, compareLogIds }) => {
  try {
    const response = await axiosInstance.post("/telemetry/comparisons", {
      name,
      base_log_id: baseLogId,
      compare_log_ids: compareLogIds,
    });
    return response.data.comparison;
  } catch (error) {
    throw new Error(detailMessage(error, "Failed to create comparison"));
  }
};

export const deleteComparison = async (id) => {
  try {
    await axiosInstance.delete(`/telemetry/comparisons/${id}`);
  } catch (error) {
    throw new Error(detailMessage(error, "Failed to delete comparison"));
  }
};

/**
 * Poll getLog until status leaves 'pending'/'parsing' or attempts run out.
 * Returns the final log. The parse is a background task server-side.
 */
export const pollLogUntilDone = async (logId, { intervalMs = 2000, maxAttempts = 90, signal } = {}) => {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (signal?.aborted) throw new Error("cancelled");
    const log = await getLog(logId);
    if (log.status === "ready" || log.status === "failed") return log;
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  throw new Error("Parsing timed out");
};
